// -----------------------------------------------------------------------------
// App.js
// Root application component for Harbour Nest.
//
// RESPONSIBILITIES:
// - Define top-level routing
// - Mount global, always-on system components (e.g. VoiceConsole)
// - Act as the stable shell for all feature modules
// - Handle authentication routes and protected routes
//
// IMPORTANT:
// - VoiceConsole is mounted OUTSIDE of <Routes>
//   so voice input is available on every page.
// - Auth routes are PUBLIC (login, register, etc.)
// - All other routes are PROTECTED by PrivateRoute
// -----------------------------------------------------------------------------

import React from "react";
import { Routes, Route } from "react-router-dom";

// -----------------------------------------------------------------------------
// Auth Components
// -----------------------------------------------------------------------------
import LoginPage from "./components/auth/LoginPage.jsx";
import RegisterPage from "./components/auth/RegisterPage.jsx";
import ForgotPassword from "./components/auth/ForgotPassword.jsx";
import ResetPassword from "./components/auth/ResetPassword.jsx";
import OnboardingWizard from "./components/auth/OnboardingWizard.jsx";
import AuthCallback from "./components/auth/AuthCallback.jsx";
import VerifyEmail from "./components/auth/VerifyEmail.jsx";
import PrivateRoute from "./components/auth/PrivateRoute.jsx";

// -----------------------------------------------------------------------------
// Portfolio + Property Views
// -----------------------------------------------------------------------------
import PropertyDashboard from "./components/PropertyDashboard.js";
import PropertyDetails from "./components/PropertyDetails.js";
import PortfolioOverview from "./components/portfolio/PortfolioOverview.jsx";

// -----------------------------------------------------------------------------
// Feature Modules
// -----------------------------------------------------------------------------
import HomeRunFlow from "./components/HomeRunFlow.jsx";
import TalkToMyDocs from "./components/TalkToMyDocs.js";
import VoiceAssistant from "./components/VoiceAssistant.js";
import ServiceProviderDashboard from "./components/ServiceProviderDashboard.jsx";
import InviteAccept from "./components/auth/InviteAccept.jsx";

// -----------------------------------------------------------------------------
// Add Property
// -----------------------------------------------------------------------------
import AddProperty from "./components/AddProperty.js";

// -----------------------------------------------------------------------------
// Global Voice Console (Always-On Audio Input)
// -----------------------------------------------------------------------------
import VoiceConsole from "./components/voice/VoiceConsole.jsx";

import "./App.css";

function App() {
  return (
    <>
      {/* ---------------------------------------------------------
         GLOBAL VOICE CONSOLE
         - Always visible (only when authenticated - handled inside)
         - Push-to-talk audio input
         - Voice-first interaction layer
      --------------------------------------------------------- */}
      <VoiceConsole />

      {/* ---------------------------------------------------------
         APPLICATION ROUTES
      --------------------------------------------------------- */}
      <Routes>
        {/* =====================================================
           PUBLIC ROUTES (No authentication required)
        ===================================================== */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth-callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<OnboardingWizard />} />
        <Route path="/invite/:token" element={<InviteAccept />} />

        {/* =====================================================
           PROTECTED ROUTES (Authentication required)
        ===================================================== */}

        {/* Home / Overview */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <PortfolioOverview />
            </PrivateRoute>
          }
        />
        <Route
          path="/overview"
          element={
            <PrivateRoute>
              <PortfolioOverview />
            </PrivateRoute>
          }
        />

        {/* Properties */}
        <Route
          path="/properties"
          element={
            <PrivateRoute>
              <PropertyDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/property/:slug"
          element={
            <PrivateRoute>
              <PropertyDetails />
            </PrivateRoute>
          }
        />

        {/* Add Property */}
        <Route
          path="/add-property"
          element={
            <PrivateRoute>
              <AddProperty />
            </PrivateRoute>
          }
        />

        {/* Feature Modules */}
        <Route
          path="/homerun"
          element={
            <PrivateRoute>
              <HomeRunFlow />
            </PrivateRoute>
          }
        />
        <Route
          path="/talktome"
          element={
            <PrivateRoute>
              <TalkToMyDocs />
            </PrivateRoute>
          }
        />
        <Route
          path="/voice"
          element={
            <PrivateRoute>
              <VoiceAssistant />
            </PrivateRoute>
          }
        />

        {/* Service Provider Dashboard */}
        <Route
          path="/my-tasks"
          element={
            <PrivateRoute>
              <ServiceProviderDashboard />
            </PrivateRoute>
          }
        />

        {/* Fallback - redirect to home (which will handle auth) */}
        <Route
          path="*"
          element={
            <PrivateRoute>
              <PortfolioOverview />
            </PrivateRoute>
          }
        />
      </Routes>
    </>
  );
}

export default App;
